import { AnimationCategory, AnimationSubType, ButtonSubType } from '../types';

// Тип вкладки настроек
export type SettingsTab =
  | 'basic' // Общие настройки (скорость, размер, цвет)
  | 'movement' // Движение (направление, ветер, гравитация)
  | 'wave' // Волна (амплитуда, частота)
  | 'colors' // Цвета и палитры
  | 'depth' // Глубина и слои
  | 'cursor' // Эффекты курсора
  | 'firefly' // Настройки Firefly
  | 'button'; // Настройки кнопок

// Интерфейс определения вкладки
export interface TabDefinition {
  id: SettingsTab;
  label: string;
  icon: string;
}

// Какие вкладки доступны для какой категории
export const categoryTabs: Record<AnimationCategory, SettingsTab[]> = {
  backgrounds: ['basic', 'movement', 'wave', 'colors', 'depth', 'cursor'],
  buttons: ['basic', 'button', 'firefly'],
  text: ['basic', 'colors'],
  loaders: ['basic', 'colors'],
  cards: ['basic'],
};

// Какие настройки отображать на каждой вкладке для каждой категории/подтипа
export interface SettingConfig {
  key: string;
  label: string;
  type: 'slider' | 'toggle' | 'select' | 'color';
  min?: number;
  max?: number;
  step?: number;
  format?: (v: number) => string;
  options?: { value: string; label: string }[];
  description?: string;
}

// Настройки для вкладки Basic (общие для всех)
export const basicSettings: SettingConfig[] = [
  {
    key: 'speed',
    label: 'Скорость',
    type: 'slider',
    min: 0.1,
    max: 10,
    step: 0.1,
    format: (v) => v.toFixed(1),
  },
  {
    key: 'intensity',
    label: 'Интенсивность',
    type: 'slider',
    min: 0,
    max: 1,
    step: 0.01,
    format: (v) => v.toFixed(2),
  },
  { key: 'count', label: 'Количество', type: 'slider', min: 10, max: 500, step: 10 },
  { key: 'size', label: 'Размер', type: 'slider', min: 1, max: 30, step: 1 },
  {
    key: 'opacity',
    label: 'Прозрачность',
    type: 'slider',
    min: 0,
    max: 1,
    step: 0.01,
    format: (v) => v.toFixed(2),
  },
  {
    key: 'scaleCount',
    label: 'Масштабировать под размер',
    type: 'toggle',
    description: 'Количество частиц подстраивается под размер элемента',
  },
];

// Настройки для вкладки Movement (движение)
export const movementSettings: SettingConfig[] = [
  {
    key: 'direction',
    label: 'Направление',
    type: 'select',
    options: [
      { value: 'down', label: 'Вниз' },
      { value: 'up', label: 'Вверх' },
      { value: 'left', label: 'Влево' },
      { value: 'right', label: 'Вправо' },
      { value: 'random', label: 'Случайно' },
    ],
  },
  {
    key: 'wind',
    label: 'Ветер (горизонтально)',
    type: 'slider',
    min: -5,
    max: 5,
    step: 0.1,
    format: (v) => v.toFixed(1),
  },
  {
    key: 'gravity',
    label: 'Гравитация',
    type: 'slider',
    min: 0,
    max: 2,
    step: 0.05,
    format: (v) => v.toFixed(2),
  },
];

// Настройки для вкладки Wave (волна)
export const waveSettings: SettingConfig[] = [
  { key: 'waveEnabled', label: 'Включить волну', type: 'toggle' },
  { key: 'waveAmplitude', label: 'Амплитуда', type: 'slider', min: 1, max: 100, step: 1 },
  {
    key: 'waveFrequency',
    label: 'Частота',
    type: 'slider',
    min: 0.001,
    max: 0.1,
    step: 0.001,
    format: (v) => v.toFixed(3),
  },
  {
    key: 'waveSpeed',
    label: 'Скорость волны',
    type: 'slider',
    min: 0.1,
    max: 5,
    step: 0.1,
    format: (v) => v.toFixed(1),
  },
];

// Настройки для вкладки Colors (цвета)
export const colorsSettings: SettingConfig[] = [
  {
    key: 'colorPalette',
    label: 'Цветовая палитра',
    type: 'select',
    options: [
      { value: 'default', label: 'По умолчанию' },
      { value: 'fire', label: 'Огонь' },
      { value: 'ocean', label: 'Океан' },
      { value: 'forest', label: 'Лес' },
      { value: 'neon', label: 'Неон' },
      { value: 'rainbow', label: 'Радуга' },
      { value: 'sunset', label: 'Закат' },
    ],
  },
  { key: 'color', label: 'Свой цвет', type: 'color', description: 'Переопределяет палитру' },
];

// Настройки для вкладки Depth (глубина)
export const depthSettings: SettingConfig[] = [
  { key: 'depthEnabled', label: 'Включить глубину', type: 'toggle' },
  { key: 'depthLayers', label: 'Количество слоёв', type: 'slider', min: 2, max: 5, step: 1 },
  {
    key: 'depthFocus',
    label: 'Фокусное расстояние',
    type: 'slider',
    min: 0,
    max: 1,
    step: 0.1,
    format: (v) => `${Math.round(v * 100)}%`,
  },
  {
    key: 'depthBlur',
    label: 'Размытие',
    type: 'toggle',
    description: 'Добавляет эффект размытия для дальних слоёв (потребляет ресурсы)',
  },
];

// Настройки для вкладки Cursor (эффекты курсора)
export const cursorSettings: SettingConfig[] = [
  {
    key: 'cursorEffect',
    label: 'Эффект курсора',
    type: 'select',
    options: [
      { value: 'none', label: 'Нет' },
      { value: 'fear', label: 'Страх (убегают)' },
      { value: 'attract', label: 'Притягивание' },
      { value: 'stop', label: 'Остановка' },
      { value: 'avoid', label: 'Избегание' },
      { value: 'fade', label: 'Исчезновение' },
      { value: 'slow', label: 'Замедление' },
    ],
  },
  {
    key: 'cursorRadius',
    label: 'Радиус действия',
    type: 'slider',
    min: 30,
    max: 300,
    step: 10,
    format: (v) => `${v}px`,
  },
  { key: 'cursorStrength', label: 'Сила эффекта', type: 'slider', min: 0.1, max: 3, step: 0.1 },
  {
    key: 'cursorInertia',
    label: 'Инерция',
    type: 'slider',
    min: 0.8,
    max: 1,
    step: 0.01,
    format: (v) => `${Math.round(v * 100)}%`,
  },
];

// Настройки для Firefly
export const fireflySettings: SettingConfig[] = [
  { key: 'fireflySize', label: 'Размер огонька', type: 'slider', min: 2, max: 20, step: 1 },
  { key: 'fireflyGlow', label: 'Свечение', type: 'slider', min: 0, max: 50, step: 1 },
  {
    key: 'fireflySpeed',
    label: 'Скорость',
    type: 'slider',
    min: 0.1,
    max: 5,
    step: 0.1,
    format: (v) => v.toFixed(1),
  },
  {
    key: 'fireflyBlink',
    label: 'Мерцание',
    type: 'slider',
    min: 0,
    max: 1,
    step: 0.05,
    format: (v) => v.toFixed(2),
  },
  { key: 'fireflyTrail', label: 'Шлейф', type: 'toggle' },
  { key: 'fireflyBlur', label: 'Размытие свечения', type: 'toggle' },
  { key: 'fireflyColor', label: 'Цвет огонька', type: 'color' },
];

// Настройки для Button
export const buttonSettings: SettingConfig[] = [
  { key: 'borderWidth', label: 'Толщина бордера', type: 'slider', min: 1, max: 10, step: 1 },
  { key: 'borderRadius', label: 'Скругление углов', type: 'slider', min: 0, max: 30, step: 1 },
];

// Маппинг вкладок на их настройки
export const tabSettings: Record<SettingsTab, SettingConfig[]> = {
  basic: basicSettings,
  movement: movementSettings,
  wave: waveSettings,
  colors: colorsSettings,
  depth: depthSettings,
  cursor: cursorSettings,
  firefly: fireflySettings,
  button: buttonSettings,
};

// Проверка, показывать ли настройку для конкретного подтипа
export const isSettingVisible = (
  settingKey: string,
  category: AnimationCategory,
  subType: AnimationSubType
): boolean => {
  // Basic настройки показываем для всех
  if (['speed', 'intensity', 'opacity'].includes(settingKey)) {
    return true;
  }

  // Настройки движения для backgrounds
  if (['direction', 'wind', 'gravity'].includes(settingKey)) {
    return category === 'backgrounds';
  }

  // Настройки волны для backgrounds
  if (['waveEnabled', 'waveAmplitude', 'waveFrequency', 'waveSpeed'].includes(settingKey)) {
    return category === 'backgrounds';
  }

  // Настройки глубины для backgrounds
  if (['depthEnabled', 'depthLayers', 'depthFocus', 'depthBlur'].includes(settingKey)) {
    return category === 'backgrounds';
  }

  // Настройки курсора для backgrounds
  if (['cursorEffect', 'cursorRadius', 'cursorStrength', 'cursorInertia'].includes(settingKey)) {
    return category === 'backgrounds';
  }

  // Firefly настройки для buttons/firefly
  if (settingKey.startsWith('firefly')) {
    return category === 'buttons' && subType === 'firefly';
  }

  // Border Glow настройки для buttons/border-glow
  if (settingKey.startsWith('glow')) {
    return category === 'buttons' && subType === 'border-glow';
  }

  // Button настройки для buttons
  if (['borderWidth', 'borderRadius'].includes(settingKey)) {
    return category === 'buttons';
  }

  // Количество и размер для backgrounds
  if (['count', 'size', 'scaleCount'].includes(settingKey)) {
    return category === 'backgrounds';
  }

  // Цветовые настройки для backgrounds, text, loaders
  if (['colorPalette', 'color'].includes(settingKey)) {
    return ['backgrounds', 'text', 'loaders', 'buttons'].includes(category);
  }

  return true;
};

// Получить доступные вкладки для текущего пресета
export const getAvailableTabs = (category: AnimationCategory): SettingsTab[] => {
  return categoryTabs[category] || ['basic'];
};

// Получить настройки для вкладки с учётом видимости
export const getVisibleSettings = (
  tab: SettingsTab,
  category: AnimationCategory,
  subType: AnimationSubType
): SettingConfig[] => {
  const settings = tabSettings[tab] || [];
  return settings.filter((s) => isSettingVisible(s.key, category, subType));
};
